import { z } from 'zod';
import type { RouterNavEntry } from '@/routing/nav-from-router';

/**
 * "Resume where you left off" for sidebar sections that opt in with
 * `nav.resumeLastVisited`: the last location the reader was at inside the
 * section, per browser tab, so leaving for another page and clicking the
 * section again returns there (the review you were in, not the Reviews list).
 */
const NavResumeTargetSchema = z.object({ pathname: z.string() });
export type NavResumeTarget = z.infer<typeof NavResumeTargetSchema>;

const STORAGE_PREFIX = 'nav-resume:';

function isInSection(pathname: string, sectionPath: string): boolean {
  return pathname === sectionPath || pathname.startsWith(`${sectionPath}/`);
}

/** Remember `target` as the last place visited inside `sectionPath`. */
export function rememberNavLocation(
  sectionPath: string,
  target: NavResumeTarget,
): void {
  try {
    sessionStorage.setItem(
      STORAGE_PREFIX + sectionPath,
      JSON.stringify(target),
    );
  } catch {
    // Storage blocked (private window, quota): the section just starts over.
  }
}

function rememberedNavLocation(sectionPath: string): NavResumeTarget | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_PREFIX + sectionPath);
    if (raw === null) return null;
    const parsed = NavResumeTargetSchema.safeParse(JSON.parse(raw));
    return parsed.success && isInSection(parsed.data.pathname, sectionPath)
      ? parsed.data
      : null;
  } catch {
    return null;
  }
}

/**
 * Where each opted-in sidebar entry should link. Inside the section (it is the
 * best match for the current path) the entry keeps its own path, so clicking it
 * there still reaches the section's landing page — the Reviews list. Outside,
 * it links to the remembered location, when there is one.
 *
 * `pathname` is taken so a navigation re-reads storage: what was remembered
 * while inside is what the render after leaving has to see.
 */
export function navResumeTargets(
  entries: readonly RouterNavEntry[],
  bestMatch: string | null,
  pathname: string,
): ReadonlyMap<string, NavResumeTarget> {
  const targets = new Map<string, NavResumeTarget>();
  for (const entry of entries) {
    if (!entry.meta.nav?.resumeLastVisited) continue;
    if (entry.fullPath === bestMatch || isInSection(pathname, entry.fullPath)) {
      continue;
    }
    const target = rememberedNavLocation(entry.fullPath);
    if (target) targets.set(entry.fullPath, target);
  }
  return targets;
}
